import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { absoluteAssetUrl, canonicalUrl, isIndexablePublicPath, productionHostname } from "@/lib/seo";

function upsertMeta(selector, attributes) {
  let node = document.head.querySelector(selector);
  if (!node) {
    node = document.createElement("meta");
    document.head.appendChild(node);
  }
  Object.entries(attributes).forEach(([key, value]) => node.setAttribute(key, value));
}

export function SeoPolicy() {
  const location = useLocation();
  useEffect(() => {
    const indexable = productionHostname(window.location.hostname) && isIndexablePublicPath(location.pathname);
    upsertMeta('meta[name="robots"]', { name: "robots", content: indexable ? "index,follow" : "noindex,nofollow" });
  }, [location.pathname]);
  return null;
}

export default function Seo({ title, description, path, image, type = "website", schemas = [], noIndex = false }) {
  useEffect(() => {
    const url = canonicalUrl(path);
    const socialImage = absoluteAssetUrl(image);
    document.title = title;
    upsertMeta('meta[name="description"]', { name: "description", content: description });
    upsertMeta('meta[property="og:title"]', { property: "og:title", content: title });
    upsertMeta('meta[property="og:description"]', { property: "og:description", content: description });
    upsertMeta('meta[property="og:type"]', { property: "og:type", content: type });
    upsertMeta('meta[property="og:url"]', { property: "og:url", content: url });
    upsertMeta('meta[property="og:image"]', { property: "og:image", content: socialImage });
    upsertMeta('meta[name="twitter:card"]', { name: "twitter:card", content: "summary_large_image" });
    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.rel = "canonical";
      document.head.appendChild(canonical);
    }
    canonical.href = url;
    if (noIndex) upsertMeta('meta[name="robots"]', { name: "robots", content: "noindex,nofollow" });
    document.head.querySelectorAll('script[data-seo-jsonld="true"]').forEach((node) => node.remove());
    (Array.isArray(schemas) ? schemas : [schemas]).filter(Boolean).forEach((schema) => {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.dataset.seoJsonld = "true";
      script.text = JSON.stringify(schema).replace(/</g, "\\u003c");
      document.head.appendChild(script);
    });
    return () => document.head.querySelectorAll('script[data-seo-jsonld="true"]').forEach((node) => node.remove());
  }, [title, description, path, image, type, schemas, noIndex]);
  return null;
}
